import { constant } from '@config/BaseSetting';
import ClientRoutes from '@config/Routes/WebClientRoutes';
import { showFixedAlert } from '@features/application/applicationSlice';
import useForm from '@hooks/useForm';
import useLocalStorage from '@hooks/useLocalStorage';
import {
  useGetInvoiceAdjustmentsMutation,
  useUpdateBillingAdjustmentAndDiscountMutation,
} from '@services/WebApiService/InvoiceApi';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

const formSchema = yup.object().shape({
  supportTicket: yup.string().default(''),
  escalationTicket: yup.string().default(''),
  description: yup.string().default(''),
});

const initialAdjustmentState = {
  original: null,
  current: null,
  loading: false,
  error: null,
};

const defaultAdjustmentData = {
  data: [{ amount: 0, reason: '' }],
  total_amount: 0,
};

export default function useBillingAdjustmentsAndDiscounts({
  showModal,
  selectedInvoice,
  onSuccessRequest,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [accessTokenSet] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const form = useForm(formSchema);
  const [errorMessage, setErrorMessage] = useState();
  const [adjustmentState, setAdjustmentState] = useState(initialAdjustmentState);
  const [getInvoiceAdjustments, getInvoiceAdjustmentsState] = useGetInvoiceAdjustmentsMutation();
  const [updateBillingAdjustmentAndDiscount, updateBillingAdjustmentAndDiscountState] =
    useUpdateBillingAdjustmentAndDiscountMutation();

  const resetAll = () => {
    setErrorMessage(undefined);
    form.reset();
    updateBillingAdjustmentAndDiscountState.reset();
    getInvoiceAdjustmentsState.reset();
    if (selectedInvoice?.id && showModal) {
      setAdjustmentState({ loading: true });
      getInvoiceAdjustments({ accessToken: accessTokenSet, invoiceId: selectedInvoice.id });
    } else {
      setAdjustmentState(initialAdjustmentState);
    }
  };

  useEffect(() => {
    resetAll();
  }, [selectedInvoice, showModal]);

  useEffect(() => {
    if (!form.isValid) return;
    updateBillingAdjustmentAndDiscount({
      accessToken: accessTokenSet,
      parameters: {
        'invoice-id': selectedInvoice.id,
        'support-ticket': form.values.supportTicket,
        'escalation-ticket': form.values.escalationTicket,
        description: form.values.description,
        'billing-adjustments': adjustmentState.current?.billingAdjustments?.data?.filter(
          (item) => item.amount && item.reason
        ),
        discounts: adjustmentState.current?.discounts?.data?.filter(
          (item) => item.amount && item.reason
        ),
      },
    });
  }, [form.isValid]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    form.validate();
  };

  useEffect(() => {
    if (updateBillingAdjustmentAndDiscountState.data?.['status-code'] === 200) {
      if (updateBillingAdjustmentAndDiscountState.data?.data?.serviceTypeCode) {
        const link = ClientRoutes.toolsRequestStatusPath({
          'service-type': updateBillingAdjustmentAndDiscountState.data?.data?.serviceTypeCode,
        });
        dispatch(
          showFixedAlert({
            descriptionHtml: t(
              'invoices_page.billing_adjustments_and_discounts.success_submitted_with_link',
              {
                link,
              }
            ),
          })
        );
      } else {
        dispatch(
          showFixedAlert({
            descriptionHtml: t('invoices_page.billing_adjustments_and_discounts.success_submitted'),
          })
        );
      }
      if (onSuccessRequest) onSuccessRequest();
      resetAll();
    } else if (updateBillingAdjustmentAndDiscountState.error) {
      form.reset(true);
      setErrorMessage(
        updateBillingAdjustmentAndDiscountState.error?.data?.message ||
          updateBillingAdjustmentAndDiscountState.error?.data?.['status-code'] ||
          updateBillingAdjustmentAndDiscountState.error?.status ||
          (updateBillingAdjustmentAndDiscountState.error.name === 'FetchError'
            ? 'Network error occurred'
            : 'Unknown error occurred')
      );
    }
  }, [updateBillingAdjustmentAndDiscountState.data, updateBillingAdjustmentAndDiscountState.error]);

  useEffect(() => {
    if (
      getInvoiceAdjustmentsState.data?.['status-code'] === 200 &&
      getInvoiceAdjustmentsState.data?.data
    ) {
      const original = {
        billingAdjustments: getInvoiceAdjustmentsState.data?.data.billingAdjustments?.data?.length
          ? getInvoiceAdjustmentsState.data?.data.billingAdjustments
          : defaultAdjustmentData,
        discounts: getInvoiceAdjustmentsState.data?.data.discounts?.data?.length
          ? getInvoiceAdjustmentsState.data?.data.discounts
          : defaultAdjustmentData,
      };
      setAdjustmentState({
        original,
        current: original,
        loading: false,
      });
    } else if (getInvoiceAdjustmentsState.error) {
      const error =
        getInvoiceAdjustmentsState.error?.data?.message ||
        getInvoiceAdjustmentsState.error?.data?.['status-code'] ||
        getInvoiceAdjustmentsState.error?.status ||
        (getInvoiceAdjustmentsState.error.name === 'FetchError'
          ? 'Network error occurred'
          : 'Unknown error occurred');
      setAdjustmentState({
        error,
      });
    }
  }, [getInvoiceAdjustmentsState.data, getInvoiceAdjustmentsState.error]);

  const addAdjustment = useCallback(
    (adjustmentType) => {
      if (!Array.isArray(adjustmentState.current?.[adjustmentType]?.data)) return;
      const data = [...adjustmentState.current[adjustmentType].data, { amount: 0, reason: '' }];
      setAdjustmentState({
        ...adjustmentState,
        current: {
          ...adjustmentState.current,
          [adjustmentType]: {
            data,
            total_amount: adjustmentState.current?.[adjustmentType].total_amount,
          },
        },
      });
    },
    [adjustmentState]
  );

  const removeAdjustment = useCallback(
    (adjustmentType, index) => {
      if (index < 0) return;
      if (!Array.isArray(adjustmentState.current?.[adjustmentType]?.data)) return;
      if (index >= adjustmentState.current[adjustmentType].data.length) return;

      const data = [...adjustmentState.current[adjustmentType].data];
      if (data.length <= 1) {
        setAdjustmentState({
          ...adjustmentState,
          current: {
            ...adjustmentState.current,
            [adjustmentType]: defaultAdjustmentData,
          },
        });
        return;
      }

      data.splice(index, 1);

      // Calculate total amount
      let totalAmount = 0;
      for (let i = 0; i < data.length; i += 1) {
        const amount = Number.parseFloat(data[i].amount);
        if (!Number.isNaN(amount)) totalAmount += amount;
      }

      setAdjustmentState({
        ...adjustmentState,
        current: {
          ...adjustmentState.current,
          [adjustmentType]: {
            data,
            total_amount: totalAmount,
          },
        },
      });
    },
    [adjustmentState]
  );

  const updateAdjustment = useCallback(
    (adjustmentType, index, field, value) => {
      if (index < 0) return;
      if (!Array.isArray(adjustmentState.current?.[adjustmentType]?.data)) return;
      if (index >= adjustmentState.current[adjustmentType].data.length) return;
      const data = [...adjustmentState.current[adjustmentType].data];
      data[index] = {
        ...data[index],
        [field]: value,
      };

      // Calculate total amount
      let totalAmount = adjustmentState.current[adjustmentType].total_amount;
      if (field === 'amount') {
        totalAmount = 0;
        for (let i = 0; i < data.length; i += 1) {
          const amount = Number.parseFloat(data[i].amount);
          if (!Number.isNaN(amount)) totalAmount += amount;
        }
      }

      setAdjustmentState({
        ...adjustmentState,
        current: {
          ...adjustmentState.current,
          [adjustmentType]: {
            data,
            total_amount: totalAmount,
          },
        },
      });
    },
    [adjustmentState]
  );

  return {
    adjustmentState,
    addAdjustment,
    removeAdjustment,
    updateAdjustment,
    isLoading: updateBillingAdjustmentAndDiscountState.isLoading,
    errorMessage,
    form,
    resetAll,
    handleSubmit,
  };
}
